import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
// REDUX
import { useAppDispatch } from 'slices/store';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import { Alert, Form, Input, List } from '@timeedit/ui-components/antd';
// COMPONENTS
import LoadingWrapper from '../../../components/LoadingWrapper';
// i18N
import intl from '../../../i18n/intl';
import {
  appsIntegrationSettingsLoading,
  appsIntegrationSettingsSelector,
  fetchAppsIntegrationSettings,
  saveAppsIntegrationSettings,
} from '../../../slices/appsIntegrationSettings.slice';

const language = intl.messages as Record<string, string>;

const IntegrationSettingsPage = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const isLoading = useSelector(appsIntegrationSettingsLoading);
  // Store local copy of integration settings in component state
  const storeResource = useSelector(appsIntegrationSettingsSelector);
  const [resource, setResource] = useState(storeResource);

  const languages = intl.messages as Record<string, string>;

  /**
   * EFFECTS
   */
  useEffect(() => {
    setResource(storeResource);
  }, [storeResource]);

  useEffect(() => {
    dispatch(fetchAppsIntegrationSettings());
  }, []);

  /**
   * EVENT HANDLERS
   */
  const onSaveChanges = () => {
    dispatch(saveAppsIntegrationSettings(resource));
  };

  const onDiscardChanges = () => {
    setResource(storeResource);
  };

  const onChange = (prop: string, value: string) => {
    setResource({ ...resource, [prop]: value });
  };

  const actions = [
    <Button key="discard_changes" size="middle" onClick={onDiscardChanges}>
      {language.discard_changes}
    </Button>,
    <Button
      key="save_changes"
      type="primary"
      size="middle"
      disabled={
        !resource.baseUrl ||
        !resource.username ||
        !resource.password ||
        !resource.certificateKey
      }
      onClick={onSaveChanges}
    >
      {language.save_changes}
    </Button>,
  ];

  return (
    <DefaultContentLayout
      title={languages.application_integration_setting_title}
      subtitle={languages.application_integration_setting_subtitle}
      actions={actions}
    >
      <div
        className="inner-content--wrapper allow-scroll"
        style={{ padding: '10px' }}
      >
        <LoadingWrapper isLoading={isLoading}>
          <>
            <Alert
              style={{ marginBottom: '2rem' }}
              message={languages.integration_settings_danger_zone}
              description={
                languages.integration_settings_danger_zone_description
              }
              type="error"
            />
            <Form form={form}>
              <List
                header={languages.integration_settings_header}
                dataSource={[
                  {
                    title: languages.integration_settings_base_url,
                    description:
                      languages.integration_settings_base_url_description,
                    key: 'baseUrl',
                    type: 'text',
                    rules: [
                      {
                        required: true,
                        message:
                          languages.integration_settings_base_url_required as string,
                      },
                    ],
                  },
                  {
                    title: languages.integration_settings_username,
                    description:
                      languages.integration_settings_username_description,
                    key: 'username',
                    type: 'text',
                    rules: [
                      {
                        required: true,
                        message:
                          languages.integration_settings_username_required as string,
                      },
                    ],
                  },
                  {
                    title: languages.integration_settings_password,
                    description:
                      languages.integration_settings_password_description,
                    key: 'password',
                    type: 'password',
                    rules: [
                      {
                        required: true,
                        message:
                          languages.integration_settings_password_required as string,
                      },
                    ],
                  },
                  {
                    title: languages.integration_settings_certificate,
                    description:
                      languages.integration_settings_certificate_description,
                    key: 'certificateKey',
                    type: 'text',
                    rules: [
                      {
                        required: true,
                        message:
                          languages.integration_settings_certificate_required as string,
                      },
                    ],
                  },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.title}
                      description={item.description}
                    />
                    <Form.Item rules={item.rules}>
                      <Input
                        style={{ maxWidth: '40rem', width: '40rem' }}
                        type={item.type}
                        disabled={false}
                        suffix={null}
                        value={_.get(resource, item.key)}
                        onChange={(e) => onChange(item.key, e.target.value)}
                      />
                    </Form.Item>
                  </List.Item>
                )}
              />
            </Form>
          </>
        </LoadingWrapper>
      </div>
    </DefaultContentLayout>
  );
};

export default IntegrationSettingsPage;
